import React from 'react';
import { EditInput, Loader } from 'react-inv-forms';
import { useTranslation } from 'react-i18next';

import marksActions from '../../../../redux/marks/actions';
import { UniqueData, MarksActionTypes } from '../../../../redux/marks/types';
import { Id } from '../../../../interfaces/global';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { RootState } from '../../../../redux/reducers';
import Moment from 'moment-timezone';
import { Mark } from './FinalMark.style';
import { DisabledContent } from '../../../../styles/sharedUIElemStyles';

import useDateOperations from '../../../../hooks/useDateOperations';

const { createFinalMark, updateFinalMark, deleteFinalMark } = marksActions;

interface EditInputProps extends MapStateToProps, MapDispatchToProps {
  /** wartość */
  value: string;
  /** obiekt z danymi, do których powiązana jest ocena końcowa */
  row: any;
  /** akcja aktualizacji danych */
  refreshCallback?: any;
  /** zestaw informacji o zakładce, w której osadzony jest komponent */
  tab: any;
}
interface MapStateToProps {
  /** stan ładowania */
  loading: boolean;
  /** aktualny język aplikacji */
  currentLanguage: string;
}
export interface FinalMarkTableCellTypes extends EditInputProps {
  type: 'final_mark';
}

export interface Forma {
  kod: string;
  nazwa: string;
}

type FinalMarkAction = (data: UniqueData, tabId: number, studentId: Id, refreshDataAction: any) => MarksActionTypes;

interface MapDispatchToProps {
  /** akcja utworzenia oceny końcowej */
  createFinalMark: FinalMarkAction;
  /** akcja edycji oceny końcowej */
  updateFinalMark: FinalMarkAction;
  /** akcja usunęcia oceny końcowej */
  deleteFinalMark: FinalMarkAction;
}

/**
 * Komponent generujący UI do wystawiania oceny
 *
 */
export const FinalMark = ({
  value,
  row,
  row: { marks, details, dostep },
  tab,
  loading,
  createFinalMark,
  updateFinalMark,
  deleteFinalMark,
  refreshCallback,
  currentLanguage,
}: EditInputProps): JSX.Element => {
  const { t } = useTranslation();
  const { generateDefaultDate } = useDateOperations();
  // info: w przypadku, gdy nie ma zdefiniowanych form zajęć dla przedmiotu - należy umożliwić wpisanie oceny końcowej
  const hasAccessToModifyObj = dostep ? dostep.czyKierownik : true;
  Moment.tz.setDefault('Europe/Warsaw');
  const onConfirm = (value: string, mode: string, date: any, event: Event): void => {
    const middayPLUnix = date.set({ hour: 12, minute: 0, second: 0, millisecond: 0 }).unix();
    const data: UniqueData = {
      kodWydzialu: tab.wydzialZlecajacyKod,
      typKartyPrzedmiotu: marks.kartaTypKod,
      kodPrzedmiotu: details.przedmiotKod.replace(/ /g, '-'),
      semestrAkademicki: details.semestrAkademicki,
      rokAkademicki: details.rokAkademicki,
      kartaPrzedmiotId: marks.kartaOcenId,
      ocena: value.toString(),
      obligatoryjnaDataWystawienia: middayPLUnix,
      typPrzydzialu: tab.typPrzydzialu,
    };
    const studentId = details.studentId;
    mode === 'add'
      ? createFinalMark(data, tab.tabId, studentId, refreshCallback)
      : updateFinalMark(data, tab.tabId, studentId, refreshCallback);
  };
  const onDelete = (): void => {
    const data: UniqueData = {
      kodWydzialu: tab.wydzialZlecajacyKod,
      typKartyPrzedmiotu: marks.kartaTypKod,
      kodPrzedmiotu: details.przedmiotKod.replace(/ /g, '-'),
      semestrAkademicki: details.semestrAkademicki,
      rokAkademicki: details.rokAkademicki,
      kartaPrzedmiotId: marks.kartaOcenId,
      ocena: '-',
      obligatoryjnaDataWystawienia: details.ocenaKoncowaDataTimestamp,
      typPrzydzialu: tab.typPrzydzialu,
    };
    const studentId = details.studentId;
    deleteFinalMark(data, tab.tabId, studentId, refreshCallback);
  };
  return (
    <>
      {loading && (
        <DisabledContent>
          <Loader />
        </DisabledContent>
      )}
      <Mark data-cy='finalMark'>
        <EditInput
          value={value}
          onConfirm={onConfirm}
          onDelete={onDelete}
          isEditable={hasAccessToModifyObj}
          noMarkText={t('NO_MARK')}
          confirmText={t('ARE_YOU_SURE')}
          pickMarkText={t('PICK_MARK')}
          dateDefaultValue={generateDefaultDate(details.ocenaKoncowaDataTimestamp)}
          isMarkNumeral={marks.przedmiotTypOceny === 'l'}
          currentLanguage={currentLanguage}
        />
      </Mark>
      <Mark>
        <em>{details.wpisujacyOceneKoncowaNazwa ? `${details.wpisujacyOceneKoncowaNazwa}` : null}</em>
        <em>{details.ocenaKoncowaDataTimestamp ? `, ${Moment(details.ocenaKoncowaDataTimestamp, 'X').format('L')}` : null}</em>
      </Mark>
    </>
  );
};

const mapDispatchToProps: MapDispatchToProps = {
  createFinalMark,
  updateFinalMark,
  deleteFinalMark,
};

const mapStateToProps = (state: RootState): MapStateToProps => ({
  loading: state.Marks.finalMarks.loading,
  currentLanguage: state.AppConfig.currentLanguage,
});

export default compose<any>(connect(mapStateToProps, mapDispatchToProps))(FinalMark);
